import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminQuickNav from '../../components/AdminQuickNav';
import { contactLinksService } from '../../services/contactLinksService';
import type { ContactLink, ContactIcon, ContactCategory } from '../../types/contact';
import { CATEGORY_LABELS } from '../../types/contact';
import {
  HiPlus,
  HiTrash,
  HiPencilAlt,
  HiEye,
  HiEyeOff,
  HiArrowUp,
  HiArrowDown,
  HiExternalLink,
  HiCheck,
  HiX,
  HiCode,
  HiSparkles,
  HiPhone,
  HiMail,
  HiGlobeAlt,
  HiLink,
} from 'react-icons/hi';
import {
  FaTelegramPlane,
  FaInstagram,
  FaDiscord,
  FaYoutube,
  FaTwitter,
  FaGithub,
} from 'react-icons/fa';

const ICONS: { key: ContactIcon; label: string; icon: React.ReactNode; color: string }[] = [
  { key: 'telegram', label: 'تلگرام', icon: <FaTelegramPlane className="w-5 h-5" />, color: 'text-sky-400' },
  { key: 'instagram', label: 'اینستاگرام', icon: <FaInstagram className="w-5 h-5" />, color: 'text-pink-500' },
  { key: 'email', label: 'ایمیل', icon: <HiMail className="w-5 h-5" />, color: 'text-amber-400' },
  { key: 'phone', label: 'شماره تماس', icon: <HiPhone className="w-5 h-5" />, color: 'text-emerald-400' },
  { key: 'discord', label: 'دیسکورد', icon: <FaDiscord className="w-5 h-5" />, color: 'text-indigo-400' },
  { key: 'youtube', label: 'یوتیوب', icon: <FaYoutube className="w-5 h-5" />, color: 'text-red-500' },
  { key: 'x', label: 'ایکس / توییتر', icon: <FaTwitter className="w-5 h-5" />, color: 'text-cyan-400' },
  { key: 'github', label: 'گیت‌هاب', icon: <FaGithub className="w-5 h-5" />, color: 'text-gray-300' },
  { key: 'globe', label: 'وب‌سایت', icon: <HiGlobeAlt className="w-5 h-5" />, color: 'text-blue-400' },
  { key: 'link', label: 'لینک عمومی', icon: <HiLink className="w-5 h-5" />, color: 'text-primary' },
];

export function renderContactIcon(iconKey: ContactIcon, className = 'w-6 h-6') {
  switch (iconKey) {
    case 'telegram':
      return <FaTelegramPlane className={`${className} text-sky-400`} />;
    case 'instagram':
      return <FaInstagram className={`${className} text-pink-500`} />;
    case 'email':
      return <HiMail className={`${className} text-amber-400`} />;
    case 'phone':
      return <HiPhone className={`${className} text-emerald-400`} />;
    case 'discord':
      return <FaDiscord className={`${className} text-indigo-400`} />;
    case 'youtube':
      return <FaYoutube className={`${className} text-red-500`} />;
    case 'x':
      return <FaTwitter className={`${className} text-cyan-400`} />;
    case 'github':
      return <FaGithub className={`${className} text-gray-300`} />;
    case 'globe':
      return <HiGlobeAlt className={`${className} text-blue-400`} />;
    case 'link':
    default:
      return <HiLink className={`${className} text-primary`} />;
  }
}

export default function ContactLinksAdminPage() {
  const [links, setLinks] = useState<ContactLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showSql, setShowSql] = useState(false);

  // Form State
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [value, setValue] = useState('');
  const [icon, setIcon] = useState<ContactIcon>('telegram');
  const [category, setCategory] = useState<ContactCategory>('social');
  const [isActive, setIsActive] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await contactLinksService.getAll();
      setLinks(data);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'خطا در بارگذاری لینک‌های تماس.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const unsub = contactLinksService.subscribe(() => {
      contactLinksService.getAll().then(setLinks);
    });
    return unsub;
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setLabel('');
    setUrl('');
    setValue('');
    setIcon('telegram');
    setCategory('social');
    setIsActive(true);
  };

  const handleEdit = (link: ContactLink) => {
    setEditingId(link.id);
    setLabel(link.label);
    setUrl(link.url);
    setValue(link.value);
    setIcon(link.icon);
    setCategory(link.category);
    setIsActive(link.is_active);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !url.trim()) {
      setMsg({ type: 'error', text: 'لطفاً عنوان و آدرس لینک را وارد کنید.' });
      return;
    }

    setSaving(true);
    setMsg(null);

    try {
      if (editingId) {
        await contactLinksService.update(editingId, {
          label: label.trim(),
          url: url.trim(),
          value: value.trim() || label.trim(),
          icon,
          category,
          is_active: isActive,
        });
        setMsg({ type: 'success', text: 'لینک تماس با موفقیت ویرایش شد.' });
      } else {
        const nextOrder = links.length > 0 ? Math.max(...links.map((l) => l.sort_order)) + 1 : 1;
        await contactLinksService.create({
          label: label.trim(),
          url: url.trim(),
          value: value.trim() || label.trim(),
          icon,
          category,
          sort_order: nextOrder,
          is_active: isActive,
        });
        setMsg({ type: 'success', text: 'لینک تماس جدید با موفقیت اضافه شد.' });
      }

      resetForm();
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'خطا در ذخیره اطلاعات.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, itemLabel: string) => {
    if (!confirm(`آیا از حذف «${itemLabel}» اطمینان دارید؟`)) return;
    try {
      await contactLinksService.delete(id);
      setMsg({ type: 'success', text: `«${itemLabel}» با موفقیت حذف شد.` });
      await loadData();
    } catch (err: any) {
      alert(err.message || 'خطا در حذف لینک.');
    }
  };

  const handleToggleActive = async (link: ContactLink) => {
    try {
      await contactLinksService.update(link.id, { is_active: !link.is_active });
      await loadData();
    } catch (err: any) {
      alert(err.message || 'خطا در تغییر وضعیت.');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= links.length) return;

    const newLinks = [...links];
    const [moved] = newLinks.splice(index, 1);
    newLinks.splice(targetIndex, 0, moved);

    setLinks(newLinks);
    await contactLinksService.reorder(newLinks.map((l) => l.id));
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-4 md:px-8" dir="rtl">
      <Helmet>
        <title>مدیریت راه‌های ارتباطی | پنل ادمین Active Legend</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <AdminQuickNav />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/20 rounded-2xl text-primary border border-primary/30">
              <HiGlobeAlt className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white">
                مدیریت راه‌های ارتباطی (تماس با ما)
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                ایجاد، ویرایش، حذف و مرتب‌سازی پیوندهای شبکه‌های اجتماعی، ایمیل‌ها و شماره‌های تماس
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowSql(!showSql)}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-xs text-gray-300 px-4 py-2.5 rounded-xl border border-white/10 transition-all self-start md:self-auto"
          >
            <HiCode className="w-4 h-4 text-primary" />
            <span>اسکریپت دیتابیس Supabase</span>
          </button>
        </div>

        {/* Cloud Database Status Warning Banner */}
        {!contactLinksService.isCloudConnected && (
          <div className="bg-amber-950/80 border-2 border-amber-500/60 rounded-3xl p-6 mb-8 shadow-2xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl shrink-0 mt-0.5">
                  <HiCode className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-black text-amber-300">
                    نیاز به اجرای یک‌باره اسکریپت SQL در Supabase (جهت نمایش در تمام مرورگرها)
                  </h3>
                  <p className="text-xs text-gray-300 mt-1 leading-relaxed">
                    جدول <code>contact_links</code> در دیتابیس Supabase ایجاد نشده است. تا زمانی که این اسکریپت در سوپابیس اجرا نشود، تغییرات فقط در همین مرورگر ذخیره شده و در سایر مرورگرها یا برای کاربران نمایش داده نخواهد شد.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowSql(true)}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold px-5 py-2.5 rounded-xl text-xs shadow-lg transition-all shrink-0"
              >
                <span>مشاهده و کپی کد SQL</span>
              </button>
            </div>
          </div>
        )}

        {/* Notification */}
        {msg && (
          <div
            className={`p-4 rounded-2xl mb-6 text-sm font-medium border flex items-center justify-between ${
              msg.type === 'success'
                ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                : 'bg-red-950/60 border-red-500/40 text-red-300'
            }`}
          >
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} className="text-white/60 hover:text-white">
              <HiX className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* SQL Script Collapsible */}
        {showSql && (
          <div className="bg-zinc-900 border border-primary/40 rounded-3xl p-6 mb-8 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <HiCode className="w-5 h-5" />
                کد ساخت جدول contact_links در کنسول Supabase (اختیاری)
              </div>
              <button
                onClick={() => setShowSql(false)}
                className="text-gray-400 hover:text-white text-xs px-2 py-1 bg-white/5 rounded-lg"
              >
                بستن
              </button>
            </div>
            <p className="text-xs text-gray-300 mb-3 leading-relaxed">
              این ماژول حتی بدون اجرای SQL به شکل پایدار با حافظه کش همگام‌سازی می‌شود، اما جهت ذخیره در دیتابیس ابری می‌توانید کد زیر را در SQL Editor سوپابیس اجرا کنید:
            </p>
            <pre className="bg-black/80 border border-white/10 rounded-2xl p-4 text-xs font-mono text-emerald-400 overflow-x-auto text-left" dir="ltr">
{`CREATE TABLE IF NOT EXISTS public.contact_links (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  label text NOT NULL,
  url text NOT NULL,
  value text NOT NULL,
  icon text NOT NULL DEFAULT 'link',
  category text NOT NULL DEFAULT 'social',
  sort_order int NOT NULL DEFAULT 0,
  is_active boolean NOT NULL DEFAULT true,
  created_at timestamptz DEFAULT now(),
  updated_at timestamptz DEFAULT now()
);

-- مجوز خواندن عمومی و نوشتن امن
ALTER TABLE public.contact_links ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read contact_links" ON public.contact_links FOR SELECT USING (true);
CREATE POLICY "Public insert/update/delete contact_links" ON public.contact_links FOR ALL USING (true);
`}
            </pre>
          </div>
        )}

        {/* Live Preview Card */}
        <div className="bg-gradient-to-r from-zinc-900 via-black to-zinc-900 border border-white/10 rounded-3xl p-6 mb-8 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-primary flex items-center gap-2">
              <HiSparkles className="w-4 h-4" />
              پیش‌نمایش زنده در صفحه «تماس با ما» ({links.filter((l) => l.is_active).length} لینک فعال)
            </span>
            <a
              href="/contact"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
            >
              <span>مشاهده صفحه عمومی</span>
              <HiExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {links
              .filter((l) => l.is_active)
              .slice(0, 4)
              .map((item) => (
                <div
                  key={item.id}
                  className="bg-zinc-900/90 border border-white/10 rounded-2xl p-4 flex items-center gap-3 shadow-md"
                >
                  <div className="w-10 h-10 rounded-xl bg-black/50 border border-white/10 flex items-center justify-center shrink-0">
                    {renderContactIcon(item.icon, 'w-5 h-5')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-white truncate">{item.label}</div>
                    <div className="text-[11px] text-gray-400 font-mono truncate" dir="ltr">
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 mb-10 shadow-2xl">
          <div className="flex items-center gap-2 text-primary font-bold text-sm mb-6">
            <HiPlus className="w-5 h-5" />
            {editingId ? 'ویرایش لینک ارتباطی' : 'افزودن لینک ارتباطی یا شبکه اجتماعی جدید'}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Label */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  عنوان نمایشی <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="مثال: پشتیبانی واتساپ، سرور دیسکورد"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all placeholder:text-gray-600"
                />
              </div>

              {/* URL */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  آدرس اینترنتی یا لینک مقصد <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="https://t.me/... یا mailto:... یا tel:..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all placeholder:text-gray-600"
                  dir="ltr"
                />
              </div>

              {/* Value / Handle */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  شناسه یا متن قابل کپی (Handle / Phone)
                </label>
                <input
                  type="text"
                  placeholder="مثال: @ActiveLegend_ir یا 0912..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all placeholder:text-gray-600"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-xs font-bold text-gray-300 mb-2">
                انتخاب آیکون مرتبط <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 md:grid-cols-10 gap-2">
                {ICONS.map((i) => {
                  const isSelected = icon === i.key;
                  return (
                    <button
                      key={i.key}
                      type="button"
                      onClick={() => setIcon(i.key)}
                      className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                        isSelected
                          ? 'bg-primary/20 border-primary shadow-lg shadow-primary/10'
                          : 'bg-black/40 border-white/5 hover:border-white/20 text-gray-400 hover:text-white'
                      }`}
                    >
                      <div className="mb-1.5">{i.icon}</div>
                      <span className="text-[10px] font-bold">{i.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
              {/* Category */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">دسته‌بندی</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ContactCategory)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all"
                >
                  <option value="social">شبکه‌های اجتماعی (Social)</option>
                  <option value="support">پشتیبانی و ارتباط (Support)</option>
                  <option value="business">تجاری و اداری (Business)</option>
                  <option value="other">سایر موارد (Other)</option>
                </select>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 pt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-5 h-5 rounded text-primary focus:ring-primary border-gray-600 bg-black/40"
                  />
                  <span className="text-sm font-bold text-white">فعال و قابل نمایش در وب‌سایت</span>
                </label>
              </div>
            </div>

            {/* Submit & Cancel */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all text-sm disabled:opacity-50"
              >
                {saving ? (
                  <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiCheck className="w-5 h-5" />
                )}
                <span>{editingId ? 'ذخیره ویرایش لینک' : 'افزودن لینک به لیست'}</span>
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-white/10 hover:bg-white/20 text-white font-medium px-4 py-3 rounded-2xl text-sm transition-all"
                >
                  انصراف
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Existing Links List Card */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <HiLink className="w-6 h-6 text-primary" />
                تمام راه‌های ارتباطی تعریف شده ({links.length})
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                ترتیب نمایش را می‌توانید با دکمه‌های بالا و پایین جابجا نمایید
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">در حال بارگذاری لینک‌ها...</div>
          ) : links.length === 0 ? (
            <div className="text-center py-12 text-gray-400 bg-black/30 rounded-2xl border border-white/5">
              هنوز لینکی تعریف نشده است. از فرم بالا اولین لینک را اضافه کنید.
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((item, index) => (
                <div
                  key={item.id}
                  className={`bg-black/50 border rounded-2xl p-4 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    item.is_active ? 'border-white/10 hover:border-white/20' : 'border-white/5 opacity-60'
                  }`}
                >
                  {/* Left (RTL Right): Icon & Details */}
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-zinc-800/80 border border-white/10 flex items-center justify-center shrink-0">
                      {renderContactIcon(item.icon, 'w-6 h-6')}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-white truncate">{item.label}</span>
                        <span className="text-[10px] bg-white/10 text-gray-300 px-2 py-0.5 rounded-full font-medium">
                          {CATEGORY_LABELS[item.category] || item.category}
                        </span>
                        {!item.is_active && (
                          <span className="text-[10px] bg-red-950 text-red-300 border border-red-500/40 px-2 py-0.5 rounded-full">
                            غیرفعال
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 font-mono">
                        <span className="text-primary truncate max-w-xs" dir="ltr">
                          {item.value}
                        </span>
                        <span className="text-gray-600">•</span>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-white flex items-center gap-1 underline underline-offset-4 truncate max-w-xs"
                          dir="ltr"
                        >
                          {item.url}
                          <HiExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 self-end sm:self-auto shrink-0">
                    {/* Move Up */}
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => handleMove(index, 'up')}
                      title="انتقال به بالا"
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all disabled:opacity-20"
                    >
                      <HiArrowUp className="w-4 h-4" />
                    </button>

                    {/* Move Down */}
                    <button
                      type="button"
                      disabled={index === links.length - 1}
                      onClick={() => handleMove(index, 'down')}
                      title="انتقال به پایین"
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all disabled:opacity-20"
                    >
                      <HiArrowDown className="w-4 h-4" />
                    </button>

                    {/* Toggle Active */}
                    <button
                      type="button"
                      onClick={() => handleToggleActive(item)}
                      title={item.is_active ? 'غیرفعال کردن' : 'فعال کردن'}
                      className={`p-2 rounded-xl transition-all ${
                        item.is_active
                          ? 'text-emerald-400 hover:bg-emerald-950/50'
                          : 'text-gray-500 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {item.is_active ? <HiEye className="w-4 h-4" /> : <HiEyeOff className="w-4 h-4" />}
                    </button>

                    {/* Edit */}
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      title="ویرایش"
                      className="p-2 text-gray-400 hover:text-primary hover:bg-white/10 rounded-xl transition-all"
                    >
                      <HiPencilAlt className="w-4 h-4" />
                    </button>

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id, item.label)}
                      title="حذف"
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-white/10 rounded-xl transition-all"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
